import type { TrimData } from '@/types'

export interface SpecificationCategory {
  items: string[]
}

/**
 * Parses a specification item string by finding the separator colon.
 * Handles colons inside parentheses (e.g., "Axle Ratio (:1) - Rear: 3.23")
 * @param item - The specification item string to parse
 * @returns Object with label (key) and value, or just the value if onlyValue is true
 */
export const parseSpecItem = (
  item: string,
  onlyValue: boolean = false,
): { label: string; value: string } | string => {
  if (!item) return onlyValue ? 'N/A' : { label: '', value: 'N/A' }

  // Find the separator colon - prefer one followed by space, and avoid colons inside parentheses
  // Look for pattern like "something (:1) - Rear: value" where the last colon is the separator
  let colonIndex = -1

  // First try to find ": " pattern (colon followed by space) that's not inside parentheses
  const colonSpaceIndex = item.indexOf(': ')
  if (colonSpaceIndex !== -1) {
    // Check if this colon is inside parentheses by counting open/close parentheses before it
    let openParens = 0
    for (let i = 0; i < colonSpaceIndex; i++) {
      if (item[i] === '(') openParens++
      if (item[i] === ')') openParens--
    }
    // If we're not inside parentheses, use this colon
    if (openParens === 0) {
      colonIndex = colonSpaceIndex
    }
  }

  // If no ": " found or it was inside parentheses, try finding the last colon
  if (colonIndex === -1) {
    const lastColonIndex = item.lastIndexOf(':')
    if (lastColonIndex !== -1) {
      colonIndex = lastColonIndex
    }
  }

  if (colonIndex !== -1) {
    const label = item.substring(0, colonIndex).trim()
    // Skip colon and any following space
    let valueStart = colonIndex + 1
    if (item.charAt(valueStart) === ' ') valueStart++
    const value = item.substring(valueStart).trim() || 'N/A'

    if (onlyValue) return value
    return { label, value }
  }

  // No colon found, entire string is key, value is N/A
  if (onlyValue) return 'N/A'
  return { label: item.trim(), value: 'N/A' }
}

/**
 * Extracts just the value from a specification item string
 */
export const parseSpecValue = (item: string): string => {
  const result = parseSpecItem(item, true)
  return typeof result === 'string' ? result : result.value
}

export const getSpecValue = (
  trim: TrimData | null | undefined,
  category: string,
  searchTerms: string | string[],
  alternativeCategories?: string[],
): string => {
  if (!trim) return 'N/A'

  const terms = Array.isArray(searchTerms) ? searchTerms : [searchTerms]
  const categories = [category, ...(alternativeCategories || [])]

  for (const cat of categories) {
    const items = trim.technicalSpecification?.[cat]?.items || []
    for (const term of terms) {
      const item = items.find((i: string) => i.includes(term))
      if (item) {
        const value = parseSpecValue(item)
        return value
          .replace(/<[^>]*>/g, '')
          .replace(/\s+/g, ' ')
          .trim()
      }
    }
  }

  return 'N/A'
}

export const getMpgInfo = (trim: TrimData | null | undefined): string => {
  if (!trim) return 'N/A'

  const cityMPGe = getSpecValue(
    trim,
    'Mileage',
    ['EPA MPG Equivalent - City', 'MPGe - City', 'City MPGe'],
    ['Engine'],
  )
  const hwMPGe = getSpecValue(
    trim,
    'Mileage',
    ['EPA MPG Equivalent - Hwy', 'MPGe - Hwy', 'Highway MPGe'],
    ['Engine'],
  )
  if (cityMPGe && cityMPGe !== 'N/A' && hwMPGe && hwMPGe !== 'N/A') {
    const cityMatch = cityMPGe.match(/\d+/)
    const hwMatch = hwMPGe.match(/\d+/)
    if (cityMatch && hwMatch) {
      const avg = Math.round(
        (parseInt(cityMatch[0]) + parseInt(hwMatch[0])) / 2,
      )
      return `${avg} MPGe`
    }
  }

  const combined = getSpecValue(
    trim,
    'Mileage',
    ['Fuel Economy Est-Combined', 'Combined MPG', 'EPA Combined', 'Combined'],
    ['Engine'],
  )
  if (combined && combined !== 'N/A') {
    const match = combined.match(/\d+/)
    if (match) return `${match[0]} MPG`
  }

  const city = getSpecValue(
    trim,
    'Mileage',
    ['EPA MPG - City', 'City MPG', 'EPA Fuel Economy Est - City'],
    ['Engine'],
  )
  const highway = getSpecValue(
    trim,
    'Mileage',
    ['EPA MPG - Hwy', 'Highway MPG', 'EPA Fuel Economy Est - Hwy'],
    ['Engine'],
  )
  if (city && city !== 'N/A' && highway && highway !== 'N/A') {
    const cityMatch = city.match(/\d+/)
    const hwMatch = highway.match(/\d+/)
    if (cityMatch && hwMatch) {
      return `${cityMatch[0]}/${hwMatch[0]} MPG`
    }
  }
  return 'MPG N/A'
}

export const getHorsepower = (trim: TrimData | null | undefined): string => {
  const extractHP = (value: string): string => {
    if (!value || value === 'N/A') return 'N/A'
    const match = value.match(/(\d+)\s*(?:hp|HP|hp\s*@|HP\s*@)/i)
    if (match) {
      return `${match[1]} HP`
    }
    const numMatch = value.match(/\d+/)
    if (numMatch) {
      return `${numMatch[0]} HP`
    }
    return value
  }

  const hp = getSpecValue(trim, 'Engine', [
    'SAE Net Horsepower',
    'Horsepower',
    'HP',
  ])
  return extractHP(hp)
}

export const getBodyStyle = (trim: TrimData | null | undefined): string => {
  return getSpecValue(trim, 'Summary', ['Body Style', 'Body Type'])
}

export const getEngineType = (trim: TrimData | null | undefined): string => {
  return getSpecValue(
    trim,
    'Engine',
    ['Engine Type', 'Engine', 'Engine Description'],
    ['Summary'],
  )
}

export const getSeatingCapacity = (
  trim: TrimData | null | undefined,
): string => {
  return getSpecValue(trim, 'Interior Dimensions', [
    'Passenger Capacity',
    'Seating Capacity',
    'Passenger',
  ])
}

export const getDrivetrain = (trim: TrimData | null | undefined): string => {
  return getSpecValue(
    trim,
    'Transmission',
    ['Drivetrain', 'Drive Type', 'Drive'],
    ['Summary'],
  )
}

export const getEngineInfo = (trim: TrimData | null | undefined): string => {
  const tech = trim?.technicalSpecification
  const engineData = tech?.Engine as SpecificationCategory | undefined
  const engine = engineData?.items?.[0]
  if (engine) {
    const match = engine.match(/(\d+\.\d+L|\d+L)/i)
    if (match) return match[0]
    const parsed = parseSpecItem(engine, false)
    return (typeof parsed === 'object' ? parsed.label : engine) || 'Engine'
  }
  return 'Engine'
}

export interface ModelDetails {
  bodyStyle: string
  engineType: string
  mpg: string
  horsepower: string
  seating: string
  drivetrain: string
}

export const getModelDetails = (
  trim: TrimData | null | undefined,
): ModelDetails => {
  return {
    bodyStyle: getBodyStyle(trim),
    engineType: getEngineType(trim),
    mpg: getMpgInfo(trim),
    horsepower: getHorsepower(trim),
    seating: getSeatingCapacity(trim),
    drivetrain: getDrivetrain(trim),
  }
}
